/**
 * Deep link parser — C16-PREP scaffolding.
 *
 * Accepts both the custom URL scheme `solidcycle://` (active today, no store
 * approval required) and the future `https://solid-cycle.mrtacojr.com/...`
 * Universal/App Link form (activated in C16-FINAL once Apple Team ID is
 * available + AASA / assetlinks.json deployed to nginx).
 *
 * Usage:
 *   const parsed = parseDeepLink('solidcycle://movimientos?focus=123');
 *   if (parsed) navigate(parsed.route, { state: parsed.params });
 *
 * Security model:
 *   - Whitelist-only routing (see {@link ROUTE_WHITELIST}). Any path not
 *     listed → null. No redirect to "/" — avoids loops if attacker manipulates.
 *   - Query params are sanitized: only [A-Za-z0-9._~%-] characters allowed,
 *     max 512 chars per value, max 8 params total. URLs > 4096 chars rejected.
 *   - Required params per route are enforced (e.g. /reset-password needs token).
 *   - Custom scheme can be hijacked by any app on the device pre-Universal-Links
 *     (iOS) — accepted risk C16-PREP. Tokens are server-validated single-use,
 *     so URL-only knowledge yields nothing. Closed C16-FINAL with Universal Links.
 *
 * Workarounds documented:
 *   - `/movimientos/:id` does NOT exist as a react-router route in C16-PREP.
 *     The whitelist exposes `/movimientos` with optional `?focus=:id` query
 *     param so the list page can scroll/highlight; a dedicated detail route
 *     can be added in C16-FINAL without changing the parser contract.
 */

// ─── Public types ────────────────────────────────────────────────────────────

/**
 * Whitelisted react-router routes reachable via deep link.
 *
 * Kept narrow (no template-literal types) so React Router's `useNavigate`
 * accepts the union directly without `as` casts.
 */
export type DeepLinkRoute =
  | '/'
  | '/onboarding'
  | '/movimientos'
  | '/reset-password'
  | '/dashboard';

export type DeepLinkSource = 'custom-scheme' | 'universal-link';

export type DeepLinkParams = Record<string, string>;

export interface ParsedDeepLink {
  /** Normalized react-router path (no query). */
  route: DeepLinkRoute;
  /** Sanitized query params (already passed safe-charset + length check). */
  params: DeepLinkParams;
  /** Origin scheme — telemetry only, never affects routing. */
  source: DeepLinkSource;
}

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * Single source of truth for deep-link-reachable routes.
 *
 * Reused by `LoginPage.tsx` `returnTo` whitelist (anti-open-redirect) — when
 * adding a route here, also confirm it's protected via `<ProtectedRoute>` in
 * App.tsx if it should require auth.
 */
export const ROUTE_WHITELIST: readonly DeepLinkRoute[] = [
  '/',
  '/onboarding',
  '/movimientos',
  '/reset-password',
  '/dashboard',
] as const;

/** Custom URL scheme (no store approval). C16-PREP active. */
const CUSTOM_SCHEME_PREFIX = 'solidcycle://';

/** Universal/App Link host for HTTPS deep links. C16-FINAL once AASA deployed. */
const UNIVERSAL_LINK_HOST = 'solid-cycle.mrtacojr.com';

/**
 * Allowed characters in a sanitized query param value (post-decodeURIComponent).
 * RFC3986 unreserved + percent (already decoded but safety in case of double-encode).
 */
const SAFE_CHARSET = /^[A-Za-z0-9._~%\- ]*$/;

/** Cap individual param value length (DoS guard). */
const MAX_PARAM_LENGTH = 512;
/** Cap number of distinct query params (DoS guard). */
const MAX_PARAM_COUNT = 8;
/** Cap full URL length (DoS guard, well above any legitimate deep link). */
const MAX_URL_LENGTH = 4096;

/**
 * Per-route required-param map. If the route is listed here, parser returns
 * null when the param is missing/empty.
 */
const REQUIRED_PARAMS: Partial<Record<DeepLinkRoute, readonly string[]>> = {
  '/reset-password': ['token'],
};

// ─── Internals ───────────────────────────────────────────────────────────────

/**
 * Returns true if a path is in the whitelist (case-sensitive).
 *
 * Exposed via {@link ROUTE_WHITELIST} for reuse; the type-narrowing helper
 * keeps callers honest about the union type.
 */
export function isWhitelistedRoute(path: string): path is DeepLinkRoute {
  return (ROUTE_WHITELIST as readonly string[]).includes(path);
}

/**
 * Sanitizes a single key/value pair. Returns null to drop the param.
 *
 * Drops any param whose key or value contains forbidden characters, or whose
 * value exceeds {@link MAX_PARAM_LENGTH}. URL-encoded characters are decoded
 * once before validation so legitimate `%20` etc. are accepted.
 */
function sanitizeParam(key: string, value: string): [string, string] | null {
  if (!key || !value) return null;
  if (value.length > MAX_PARAM_LENGTH) return null;
  // Drop keys with anything other than [A-Za-z0-9_-]
  if (!/^[A-Za-z0-9_-]{1,64}$/.test(key)) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    return null; // malformed percent-encoding → drop
  }
  if (decoded.length > MAX_PARAM_LENGTH) return null;
  if (!SAFE_CHARSET.test(decoded)) return null;
  // Reject path-traversal attempts even if the regex allowed dots
  if (decoded.includes('../') || decoded.includes('..\\')) return null;
  return [key, decoded];
}

/**
 * Normalizes a path: strips trailing slash (except root), lowercases — note
 * that React Router paths in this app are already lowercase by convention.
 */
function normalizePath(rawPath: string): string {
  if (!rawPath || rawPath === '') return '/';
  let p = rawPath;
  if (p.length > 1 && p.endsWith('/')) {
    p = p.slice(0, -1);
  }
  return p;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Parses a deep-link URL emitted by Capacitor App plugin (`appUrlOpen` event)
 * or returned by `App.getLaunchUrl()` (cold start).
 *
 * Returns:
 *   - `ParsedDeepLink` if the URL matches a known scheme + whitelisted route
 *     + passes sanitization + has all required params.
 *   - `null` for any rejection (unknown scheme, unknown route, malformed URL,
 *     missing required param, sanitization failure, length DoS guard tripped).
 *
 * The function is pure — no side effects, no React deps. Logging the reason
 * for null (e.g. via `console.warn`) is the caller's job (`mobileBootstrap`
 * does it under `verbose` flag).
 */
export function parseDeepLink(rawUrl: string): ParsedDeepLink | null {
  if (typeof rawUrl !== 'string' || rawUrl.length === 0) return null;
  if (rawUrl.length > MAX_URL_LENGTH) return null;

  let source: DeepLinkSource;
  let pathAndQuery: string;

  // Detect scheme prefix (case-insensitive on scheme only).
  const lower = rawUrl.toLowerCase();
  if (lower.startsWith(CUSTOM_SCHEME_PREFIX)) {
    source = 'custom-scheme';
    pathAndQuery = rawUrl.slice(CUSTOM_SCHEME_PREFIX.length);
  } else if (
    lower.startsWith(`https://${UNIVERSAL_LINK_HOST}/`) ||
    lower === `https://${UNIVERSAL_LINK_HOST}`
  ) {
    source = 'universal-link';
    // Strip protocol+host, leave path + query
    const idx = rawUrl.toLowerCase().indexOf(UNIVERSAL_LINK_HOST);
    pathAndQuery = rawUrl.slice(idx + UNIVERSAL_LINK_HOST.length);
  } else {
    return null;
  }

  // Use URL constructor for robust parsing (relative against a known base).
  // We rebuild as `https://x.invalid/<pathAndQuery>` so URL doesn't choke on
  // empty pathAndQuery for `solidcycle://`.
  let url: URL;
  try {
    const safePath = pathAndQuery.startsWith('/') ? pathAndQuery : `/${pathAndQuery}`;
    url = new URL(`https://x.invalid${safePath || '/'}`);
  } catch {
    return null;
  }

  const path = normalizePath(url.pathname);

  if (!isWhitelistedRoute(path)) return null;

  // Sanitize params.
  const params: DeepLinkParams = {};
  let count = 0;
  for (const [key, value] of url.searchParams.entries()) {
    if (count >= MAX_PARAM_COUNT) break;
    const sanitized = sanitizeParam(key, value);
    if (sanitized) {
      params[sanitized[0]] = sanitized[1];
      count += 1;
    }
  }

  // Enforce required params per route.
  const required = REQUIRED_PARAMS[path];
  if (required) {
    for (const k of required) {
      if (!params[k]) return null;
    }
  }

  return { route: path, params, source };
}
